import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Calculator, Zap, Flame, Home, TrendingUp, Leaf } from "lucide-react";
import { useSavingsCalculation } from "@/hooks/useSavingsCalculation";
import { usePaybackCalculation } from "@/hooks/usePaybackCalculation";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";

interface FormData {
  postcode: string;
  housingType: string;
  currentElectricityUsage: number;
  currentGasUsage: number;
  solarCapacity: number;
  heatPumpCapacity: number;
  insulationArea: number;
  insulationType: string;
}

export const BesparingCalculator = () => {
  const [formData, setFormData] = useState<FormData>({
    postcode: '',
    housingType: '',
    currentElectricityUsage: 3500,
    currentGasUsage: 1200,
    solarCapacity: 8,
    heatPumpCapacity: 8,
    insulationArea: 100,
    insulationType: 'roof'
  });

  const [activeTab, setActiveTab] = useState("solar");
  const { calculateAllSavings, isLoading: savingsLoading, savingsResults } = useSavingsCalculation();
  const { calculateMultiplePaybacks, isLoading: paybackLoading, paybackResults, getInvestmentCosts } = usePaybackCalculation();

  const handleInputChange = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCalculate = async () => {
    if (!formData.postcode || !formData.housingType) {
      alert('Vul alle verplichte velden in');
      return;
    }

    // Calculate savings
    await calculateAllSavings(formData);

    // Calculate payback for each measure
    const paybackData = [];
    
    if (formData.solarCapacity > 0) {
      paybackData.push({
        measure: 'Zonnepanelen',
        investmentCost: getInvestmentCosts('zonnepanelen', formData.solarCapacity),
        annualSavings: 0, // Will be updated from savings results
        subsidies: 0,
        financingType: 'cash' as const
      });
    }

    if (formData.heatPumpCapacity > 0) {
      paybackData.push({
        measure: 'Warmtepomp',
        investmentCost: getInvestmentCosts('warmtepomp'),
        annualSavings: 0,
        subsidies: 2500, // ISDE subsidy
        financingType: 'cash' as const
      });
    }

    if (formData.insulationArea > 0) {
      const insulationName = formData.insulationType === 'roof' ? 'dakisolatie' : 
                            formData.insulationType === 'wall' ? 'muurisolatie' : 'vloerisolatie';
      paybackData.push({
        measure: `${formData.insulationType === 'roof' ? 'Dak' : formData.insulationType === 'wall' ? 'Muur' : 'Vloer'}isolatie`,
        investmentCost: getInvestmentCosts(insulationName, undefined, formData.insulationArea),
        annualSavings: 0,
        subsidies: 1200, // ISDE subsidy
        financingType: 'cash' as const
      });
    }

    if (paybackData.length > 0) {
      await calculateMultiplePaybacks(paybackData);
    }
  };

  const chartData = savingsResults.map(result => ({
    name: result.measure,
    jaarlijkseBesparing: result.annualSavings,
    maandelijkseBesparing: result.monthlySavings,
    co2Reductie: result.co2Reduction
  }));

  return (
    <>
      <Helmet>
        <title>Besparingscalculator - Bereken je energiebesparing | SOL-VI</title>
        <meta name="description" content="Bereken hoeveel je bespaart met zonnepanelen, warmtepomp en isolatie. Directe berekening van jaarlijkse besparingen en terugverdientijd." />
        <meta name="keywords" content="besparingscalculator, energiebesparing, zonnepanelen besparing, warmtepomp besparing, isolatie besparing, Elst, Gelderland" />
        <link rel="canonical" href="https://sol-vi.nl/besparingscalculator" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Besparingscalculator
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Bereken direct hoeveel je bespaart met zonnepanelen, warmtepomp of isolatie. 
              Inclusief terugverdientijd en CO₂-reductie.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Woninggegevens
                </CardTitle>
                <CardDescription>
                  Vul je woninggegevens in voor een accurate berekening
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postcode">Postcode *</Label>
                    <Input
                      id="postcode"
                      placeholder="6662 AB"
                      value={formData.postcode}
                      onChange={(e) => handleInputChange('postcode', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="housingType">Woningtype *</Label>
                    <Select value={formData.housingType} onValueChange={(value) => handleInputChange('housingType', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecteer type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vrijstaand">Vrijstaand</SelectItem>
                        <SelectItem value="twee_onder_een_kap">Twee-onder-een-kap</SelectItem>
                        <SelectItem value="rijtjeshuis">Rijtjeshuis</SelectItem>
                        <SelectItem value="appartement">Appartement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="electricity">Stroomverbruik (kWh/jaar)</Label>
                    <Input
                      id="electricity"
                      type="number"
                      value={formData.currentElectricityUsage}
                      onChange={(e) => handleInputChange('currentElectricityUsage', parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="gas">Gasverbruik (m³/jaar)</Label>
                    <Input
                      id="gas"
                      type="number"
                      value={formData.currentGasUsage}
                      onChange={(e) => handleInputChange('currentGasUsage', parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <Separator />

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="solar">Zonnepanelen</TabsTrigger>
                    <TabsTrigger value="heatpump">Warmtepomp</TabsTrigger>
                    <TabsTrigger value="insulation">Isolatie</TabsTrigger>
                  </TabsList>

                  <TabsContent value="solar" className="space-y-4">
                    <div>
                      <Label htmlFor="solarCapacity">Vermogen zonnepanelen (kWp)</Label>
                      <Input
                        id="solarCapacity"
                        type="number"
                        value={formData.solarCapacity}
                        onChange={(e) => handleInputChange('solarCapacity', parseFloat(e.target.value))}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Gemiddeld 4-5 panelen per kWp
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="heatpump" className="space-y-4">
                    <div>
                      <Label htmlFor="heatPumpCapacity">Vermogen warmtepomp (kW)</Label>
                      <Input
                        id="heatPumpCapacity"
                        type="number"
                        value={formData.heatPumpCapacity}
                        onChange={(e) => handleInputChange('heatPumpCapacity', parseFloat(e.target.value))}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Gemiddeld 6-12 kW voor een rijtjeshuis
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="insulation" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="insulationType">Type isolatie</Label>
                        <Select value={formData.insulationType} onValueChange={(value) => handleInputChange('insulationType', value)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="roof">Dakisolatie</SelectItem>
                            <SelectItem value="wall">Muurisolatie</SelectItem>
                            <SelectItem value="floor">Vloerisolatie</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="insulationArea">Oppervlakte (m²)</Label>
                        <Input
                          id="insulationArea"
                          type="number"
                          value={formData.insulationArea}
                          onChange={(e) => handleInputChange('insulationArea', parseFloat(e.target.value))}
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <Button 
                  onClick={handleCalculate} 
                  className="w-full" 
                  size="lg"
                  disabled={savingsLoading || paybackLoading}
                >
                  {savingsLoading || paybackLoading ? "Berekenen..." : "Bereken Besparing"}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            <div className="space-y-6">
              {savingsResults.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Jaarlijkse Besparingen
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 mb-6">
                      {savingsResults.map((result, index) => (
                        <div key={index} className="p-4 border rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <h3 className="font-semibold text-lg">{result.measure}</h3>
                            <Badge variant="outline">
                              {result.percentage}% besparing
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Per jaar</p>
                              <p className="font-bold text-primary">€{result.annualSavings}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Per maand</p>
                              <p className="font-bold">€{result.monthlySavings}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <Leaf className="h-4 w-4 text-green-600" />
                              <div>
                                <p className="text-muted-foreground">CO₂ reductie</p>
                                <p className="font-bold text-green-600">{result.co2Reduction} kg</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {chartData.length > 0 && (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip formatter={(value, name) => [`€${value}`, name]} />
                            <Bar dataKey="jaarlijkseBesparing" fill="hsl(var(--primary))" name="Jaarlijkse besparing" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {paybackResults.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calculator className="h-5 w-5" />
                      Terugverdientijd
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {paybackResults.map((result, index) => (
                        <div key={index} className="p-4 border rounded-lg">
                          <h3 className="font-semibold text-lg mb-3">{result.measure}</h3>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-muted-foreground">Terugverdientijd</p>
                              <p className="font-bold text-primary">{result.paybackYears} jaar</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">ROI (20 jaar)</p>
                              <p className="font-bold text-green-600">{result.roi20Years}%</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Totale besparing (20 jaar)</p>
                              <p className="font-bold">€{result.totalSavings20Years.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Volgende stap</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Geïnteresseerd in een persoonlijke offerte? Neem contact met ons op.
                  </p>
                  <Button asChild>
                    <a href="/offerte">Vraag offerte aan</a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};